import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Api } from '@.api';
import { UserResource } from '@.api/methods';
import { Layout } from '@.layout';
import { Table, TableColumn, TableRow } from '@.partials';
import { Button, Checkbox, Icon } from '@.ui';
import { getAccessObjectTypeLabel } from '@/Components/Partials/User/UserForm/AccessObjectNumberMap';
import { LinkHelper } from '@/Services';
import { UserRoleEnum } from '@/Types/api/User';

import S from './User.module.scss';

export const User: React.FC = () => {
    const [user, setUser] = useState<UserResource | undefined>(undefined);

    const { id } = useParams();

    const navigator = useNavigate();

    const updateUser = () => {
        Api.users()
            .getById(Number(id))
            .onSuccess((res) => setUser(res.data));
    };

    const handleBlock = () => {
        Api.users()
            .blockById(Number(id))
            .onSuccess(() => updateUser());
    };

    const handleUnblock = () => {
        Api.users()
            .unblockById(Number(id))
            .onSuccess(() => updateUser());
    };

    useEffect(() => {
        updateUser();
    }, []);

    const createUserRoleTableColumn = (user: UserResource | undefined) => {
        if (!user) {
            return <TableColumn>{null}</TableColumn>;
        }

        if (user?.role.key === UserRoleEnum.SERVICE_PROVIDER) {
            return (
                <TableColumn>
                    <div className={S.serviceProviderRow}>
                        {user?.role?.name}:
                        {user.sp_cluster && (
                            <Link className={S.serviceProviderRow__link} to={LinkHelper.serviceProvider.storeClusterAdminDashboard(user.sp_cluster.id)}>
                                <u>{user.sp_cluster.name}</u>

                                <Icon name="monitoring-ico-brown" />
                            </Link>
                        )}
                    </div>
                </TableColumn>
            );
        }

        return <TableColumn>{user.role.name}</TableColumn>;
    };

    return (
        <Layout
            name="settings"
            breadcrumbs={[
                { name: 'Users', link: LinkHelper.settingsUsers },
                { name: user?.name ?? '', current: true },
            ]}
        >
            {user && (
                <>
                    <h1>
                        {user?.name}
                        <div>
                            <Button
                                color="transparent"
                                text={user?.status.name === 'Active' ? 'Block' : 'Unblock'}
                                onClick={user?.status.name === 'Active' ? handleBlock : handleUnblock}
                            />
                            {user?.id && <Button color="brown" text="Edit" onClick={() => navigator(LinkHelper.settingsUsersEditId(user?.id))} />}
                        </div>
                    </h1>
                    <Table>
                        <TableRow>
                            <TableColumn bold>Name</TableColumn>
                            <TableColumn>{user?.name}</TableColumn>
                        </TableRow>
                        <TableRow>
                            <TableColumn bold>Username</TableColumn>
                            <TableColumn>{user?.username}</TableColumn>
                        </TableRow>
                        <TableRow>
                            <TableColumn bold>Email</TableColumn>
                            <TableColumn>{user?.email}</TableColumn>
                        </TableRow>
                        <TableRow>
                            <TableColumn bold>Role</TableColumn>
                            {createUserRoleTableColumn(user)}
                        </TableRow>
                        <TableRow>
                            <TableColumn bold>Company</TableColumn>
                            <TableColumn>{user?.company?.name}</TableColumn>
                        </TableRow>
                        <TableRow>
                            <TableColumn bold>Access object type</TableColumn>
                            <TableColumn>{getAccessObjectTypeLabel(user?.access_object?.type)}</TableColumn>
                        </TableRow>
                        <TableRow>
                            <TableColumn bold>Status</TableColumn>
                            <TableColumn>{user?.status?.name}</TableColumn>
                        </TableRow>
                        <TableRow>
                            <TableColumn bold>Is company admin</TableColumn>
                            <TableColumn>
                                <Checkbox value={user?.is_company_admin} isDisabled />
                            </TableColumn>
                        </TableRow>
                    </Table>
                </>
            )}
        </Layout>
    );
};
